import { useRef, useState } from 'react';
import {
  type ConsentStatus,
  type ExcessPermissionTenant,
  type PermissionRef,
  type ReconsentTenant,
} from './api.ts';
import { copy, copyOutcomeText } from './clipboard.ts';
import { plural } from './format.ts';

/**
 * The two panels about what a tenant actually granted us.
 *
 * Split out of the Onboarding screen, which passed the 800-line ceiling when
 * the second one arrived, and rendered unchanged by the Permissions tab of the
 * Clients screen that replaced it. They belong together: both read the same
 * comparison between the permissions this build asks for and the ones Microsoft
 * says the tenant holds, and they are the two directions that comparison can be
 * wrong in — short of something, or holding something we withdrew.
 *
 * Neither computes anything. The server does the comparison once, in
 * `packages/jobs/src/permissionGrant.ts`, and the console restates none of it.
 */

/**
 * The consent link, or the reason there is not one.
 *
 * `consentUrl` is null on a deployment with no Entra application registered —
 * which is every deployment before the registration exists, exactly when
 * somebody is most likely to be on this screen. It was typed as a string and
 * read straight into a copy button, so the failure would have been a button
 * that silently copied nothing.
 *
 * Exported so `ChaseRow.tsx` reads the same sentence. Two copies of a string
 * an operator is meant to act on is two sentences that can end up saying
 * different things.
 */
export const NO_LINK = 'No consent link yet — this deployment has no Entra application registered.';

/**
 * Why a connected tenant cannot be shown as current. Two sentences rather than
 * one word, because the difference decides what the operator does next: one
 * customer needs asking again, the other needs nothing from anybody until a
 * scan has run and read their grant.
 */
const RECONSENT_REASON: Record<Exclude<ConsentStatus, 'current'>, string> = {
  incomplete:
    'Microsoft reports permissions missing from this tenant. The sources behind them cannot be collected until it consents again.',
  never_checked:
    'DriftWatch has not read this tenant’s permissions yet. That happens on its next scan; until then this is unknown rather than wrong.',
};

const consentedOn = (tenant: GrantedTenant): string => {
  const when = tenant.lastConsentedAt ?? tenant.consentedAt;
  return when === null
    ? 'No consent date recorded'
    : `Last consented ${new Date(when).toLocaleDateString('en-GB')}`;
};

/** Rows that carry the grant comparison — the two panels below share it. */
type GrantedTenant = ReconsentTenant | ExcessPermissionTenant;

/**
 * The permissions themselves, not a count.
 *
 * "Needs re-consent" is a status; "missing MailboxSettings.Read and
 * SecurityEvents.Read.All" is something an operator can put in the email they
 * are about to write. The API is named because two APIs may define the same
 * value and the operator is looking at a customer's consent screen.
 */
const PermissionList = ({
  label,
  permissions,
}: {
  label: string;
  permissions: readonly PermissionRef[];
}) =>
  permissions.length === 0 ? null : (
    <div className="ob-perms">
      <span className="label">{label}</span>
      <ul className="ob-perm-list">
        {permissions.map((permission) => (
          <li key={`${permission.resourceAppId}/${permission.permission}`}>
            <code className="mono">{permission.permission}</code>
            <span className="note"> · {permission.api}</span>
          </li>
        ))}
      </ul>
    </div>
  );

/**
 * The clients who have connected and are no longer on the permissions we ask
 * for.
 *
 * This is the failure the screen exists to make visible. Adding a permission to
 * the application registration puts every connected customer here at once, and
 * until each of them consents again their collection quietly narrows: the scan
 * still runs, the report still renders, and the sources behind the new
 * permission come back refused. Nothing else in the product looks wrong.
 *
 * It is not part of the Awaiting consent tab and cannot be. That tab is the
 * clients who have not connected; these have. The work is the same work — copy
 * a link, ask again — so it sits on the same screen, one tab across, and that
 * tab disappears entirely when there is nothing to do.
 *
 * Nothing here records that the link was sent. There is no reminder count to
 * keep for a client who is already connected, and the row leaves of its own
 * accord the moment they consent.
 */
export const ReconsentPanel = ({
  tenants,
  announce,
}: {
  tenants: readonly ReconsentTenant[];
  announce: (text: string) => void;
}) => (
  <section className="card ob-reconsent" aria-labelledby="ob-reconsent-heading">
    <div className="card-head">
      <h2 id="ob-reconsent-heading">Needs re-consent</h2>
      <p className="note">
        {plural(tenants.length, 'connected client')} we cannot show as being on the current
        permission set.
      </p>
    </div>

    <div className="card-body">
      <p className="note ob-chase-note">
        A Microsoft 365 tenant is granted the permissions it consented to and no more, so adding one
        needs every client to consent again. Until they do, their scans still run and the sources
        behind the new permission come back refused. Send the same kind of link you sent the first
        time.
      </p>

      <div className="tbl-wrap">
        <table className="tbl ob-tbl">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Why</th>
              <th>Send the link</th>
            </tr>
          </thead>
          {tenants.map((tenant) => (
            <ReconsentRow key={tenant.id} tenant={tenant} announce={announce} />
          ))}
        </table>
      </div>
    </div>
  </section>
);

const ReconsentRow = ({
  tenant,
  announce,
}: {
  tenant: ReconsentTenant;
  announce: (text: string) => void;
}) => {
  const [status, setStatus] = useState<string | null>(null);
  const linkRef = useRef<HTMLElement>(null);

  const copyLink = async () => {
    if (tenant.consentUrl === null) {
      setStatus(NO_LINK);
      announce(NO_LINK);
      return;
    }
    const outcome = await copy(tenant.consentUrl, linkRef.current);
    const text = copyOutcomeText(outcome, `Consent link for ${tenant.displayName}`);
    setStatus(text);
    announce(text);
  };

  return (
    <tbody className="ob-group">
      <tr>
        <td>
          <span className="ob-name">{tenant.displayName}</span>
          <span className="mono note ob-tenant-id">{tenant.entraTenantId}</span>
          <span className="note ob-since">{consentedOn(tenant)}</span>
        </td>
        <td>
          <span className="conn conn-pending">
            {tenant.consentStatus === 'incomplete' ? 'Permissions missing' : 'Not yet checked'}
          </span>
          <span className="note ob-since">
            {tenant.consentStatus === 'current'
              ? // Unreachable: the server only lists tenants it could not show
                // as current. Written out rather than asserted so a change at
                // either end degrades into a sentence instead of a crash.
                'Current.'
              : RECONSENT_REASON[tenant.consentStatus]}
          </span>
        </td>
        <td>
          <div className="toolbar ob-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => void copyLink()}>
              Copy consent link
            </button>
          </div>
        </td>
      </tr>
      <tr className="ob-detail">
        <td colSpan={3}>
          <PermissionList label="Missing" permissions={tenant.missingPermissions} />
          <div className="ob-linkfield">
            <span className="label" id={`reconsent-link-${tenant.id}`}>
              Consent link
            </span>
            <code
              ref={linkRef}
              className="mono ob-link"
              aria-labelledby={`reconsent-link-${tenant.id}`}
            >
              {tenant.consentUrl ?? NO_LINK}
            </code>
          </div>
          {status && <p className="status-inline">{status}</p>}
        </td>
      </tr>
    </tbody>
  );
};

/**
 * Permissions we hold and no longer ask for.
 *
 * The mirror of the panel above, and the one that is about us. Withdrawing a
 * permission from the application registration does not withdraw it from a
 * tenant that already granted it: the app role assignment sits in their
 * directory until somebody removes it, and we go on holding a privilege over a
 * customer's Microsoft 365 estate that we have decided we do not need.
 *
 * There is no button, and that is not an omission. Removing an app role
 * assignment is a write, and no code path in this product is permitted to
 * modify a customer tenant. So the whole remedy is a Global Administrator in
 * their own portal, and the whole of what this product can do is say which
 * tenant and which permission — which is why the panel exists rather than a log
 * line nobody reads.
 *
 * Deliberately quiet: no chip, no count in the summary strip. It is a standing
 * fact to be tidied up, not a customer waiting on anybody.
 *
 * **This panel and the `excess_permission` alert are not two answers to one
 * question, and the difference is worth holding on to.** Both go through the one
 * comparison on the server. This is the *live* list: recomputed against the
 * permissions this build asks for every time the screen loads, so withdrawing a
 * permission from the registration puts every affected client here at once. The
 * alert is the *notice*: raised from the grant read at the end of a scan,
 * deduplicated, delivered to whatever the MSP has configured, and resolved when
 * a later read no longer sees the permission. So this panel can be ahead of the
 * alerts by up to one scan, and it says so below rather than leaving somebody
 * to wonder which of the two is broken.
 */
export const ExcessPermissionPanel = ({ tenants }: { tenants: readonly ExcessPermissionTenant[] }) => (
  <section className="card ob-reconsent" aria-labelledby="ob-excess-heading">
    <div className="card-head">
      <h2 id="ob-excess-heading">Permissions we no longer ask for</h2>
      <p className="note">
        {plural(tenants.length, 'connected client')} still granting DriftWatch something it has
        stopped requesting.
      </p>
    </div>

    <div className="card-body">
      <p className="note ob-chase-note">
        Removing a permission from the DriftWatch application registration does not remove it from a
        tenant that already granted it, and consenting again will not clear it. DriftWatch cannot
        clear it either — it never writes to a customer tenant. A Global Administrator removes it
        from the DriftWatch enterprise application in their own portal.
      </p>

      <p className="note ob-chase-note">
        This list is current as of now. Each of these also raises an alert on that client, at the
        client’s next scan, so the work reaches a ticketing system rather than waiting to be noticed
        here — which means a permission withdrawn today appears here first and on the Alerts screen
        after the next scan.
      </p>

      <div className="tbl-wrap">
        <table className="tbl ob-tbl">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Held and not needed</th>
            </tr>
          </thead>
          {tenants.map((tenant) => (
            <tbody className="ob-group" key={tenant.id}>
              <tr>
                <td>
                  <span className="ob-name">{tenant.displayName}</span>
                  <span className="mono note ob-tenant-id">{tenant.entraTenantId}</span>
                  <span className="note ob-since">{consentedOn(tenant)}</span>
                </td>
                <td>
                  <PermissionList label="Granted" permissions={tenant.extraPermissions} />
                </td>
              </tr>
            </tbody>
          ))}
        </table>
      </div>
    </div>
  </section>
);
